import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inscricoesService } from '../services/inscricoes.service'
import { addToSyncQueue } from '../../../lib/offlineDb'
import type { InscricaoFilters, CreateInscricaoPayload } from '../types'

export const INSCRICOES_KEY = 'inscricoes'

function isOfflineError(err: unknown): boolean {
  if (!navigator.onLine) return true
  if (typeof err === 'object' && err !== null) {
    const e = err as Record<string, unknown>
    if (e.message === 'Network Error') return true
    if (typeof e.message === 'string' && e.message.includes('Network')) return true
  }
  return false
}

export function useInscricoes(filters: InscricaoFilters = {}) {
  return useQuery({
    queryKey: [INSCRICOES_KEY, filters],
    queryFn: () => inscricoesService.list(filters),
    enabled: true,
  })
}

export function useCreateInscricao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateInscricaoPayload) => {
      try {
        return await inscricoesService.create(data)
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'inscricoes',
            action: 'create',
            payload: data as unknown as Record<string, unknown>,
            endpoint: '/api/v1/inscricoes',
            method: 'POST',
          })
          return { queued: true, syncPending: true } as unknown as ReturnType<typeof inscricoesService.create>
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [INSCRICOES_KEY] }),
  })
}

export function useCancelarInscricao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      try {
        return await inscricoesService.cancelar(id)
      } catch (err) {
        if (isOfflineError(err)) {
          await addToSyncQueue({
            table: 'inscricoes',
            action: 'update',
            payload: { id, acao: 'cancelar' },
            endpoint: `/api/v1/inscricoes/${id}/cancelar`,
            method: 'PATCH',
          })
          return { queued: true, syncPending: true }
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [INSCRICOES_KEY] }),
  })
}

export function useTrocarModalidadeInscricao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { idmodalidade: number; idetapa: number } }) => {
      return await inscricoesService.trocarModalidade(id, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [INSCRICOES_KEY] }),
  })
}

export function useExcluirInscricao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => inscricoesService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [INSCRICOES_KEY] }),
  })
}

export function useValidacaoPreco(params: {
  idatirador: number | null
  idcampeonato: number | null
  idmodalidade: number | null
  idetapa: number | null
  quantidade: number
}) {
  const enabled =
    params.idatirador !== null &&
    params.idcampeonato !== null &&
    params.idmodalidade !== null &&
    params.idetapa !== null

  return useQuery({
    queryKey: ['validacao-preco', params],
    queryFn: () =>
      inscricoesService.validacaoPreco({
        idatirador: params.idatirador!,
        idcampeonato: params.idcampeonato!,
        idmodalidade: params.idmodalidade!,
        idetapa: params.idetapa!,
        quantidade: params.quantidade,
      }),
    enabled,
    staleTime: 0,
  })
}
